import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";

import Button from "components/Button";
import HollowButton from "components/HollowButton";

import dispatchChartService from "api/dispatchChartService";
import {
  EParcelDeliveryCategory,
  IDispatchChart,
  IDispatchListItem,
  IOutgoingCategory,
  IPendingReceptionItem,
} from "dto/Mailroom/IDispatchChart";

import { ENotificationType, notification } from "utils/notification";

import CreateDispatchChartModal from "./CreateDispatchChartModal";
import ChangeDeliveryKindModal from "./ChangeDeliveryKindModal";
import OutgoingRowMenu from "./OutgoingRowMenu";
import ProcessDispatchChartModal from "./ProcessDispatchChartModal";
import ReceptionModal from "./ReceptionModal";
import ParcelPreviewModal from "./ParcelPreviewModal";
import styles from "./outgoing.module.scss";

type SubView = "dispatch" | "charts" | "reception";

const SUBVIEW_LABELS: Record<SubView, string> = {
  dispatch: "Do wysyłki",
  charts: "Karty nadawcze",
  reception: "Zwrotki ZPO",
};

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString("pl-PL") : "—";

// Escape dla treści wstrzykiwanej do HTML wydruku (nazwy/adresy z danych).
const esc = (v?: string | null) =>
  (v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const fmtPln = (v?: number | null) =>
  v == null ? "" : `${v.toFixed(2).replace(".", ",")} zł`;

interface Props {
  searchText: string;
}

const OutgoingView = ({ searchText }: Props) => {
  const queryClient = useQueryClient();

  const { data: catData } = dispatchChartService.useGetOutgoingCategories();
  const categories: IOutgoingCategory[] = useMemo(
    () => catData?.data?.Categories ?? [],
    [catData],
  );

  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [subView, setSubView] = useState<SubView>("dispatch");
  const [postGroup, setPostGroup] = useState<number | null>(null);

  const activeCategory = useMemo(
    () => categories.find((c) => c.Category === activeCategoryId) ?? null,
    [categories, activeCategoryId],
  );

  // Domyślna kategoria = pierwsza z przesyłkami, inaczej pierwsza dostępna.
  useEffect(() => {
    if (activeCategoryId !== null || categories.length === 0) return;
    const withItems = categories.find(
      (c) =>
        c.ReadyToDispatchCount + c.ChartCount + c.PendingReceptionCount > 0,
    );
    setActiveCategoryId((withItems ?? categories[0]).Category);
  }, [categories, activeCategoryId]);

  // Podgrupa Poczty — domyślnie pierwsza.
  useEffect(() => {
    if (activeCategory?.Category === EParcelDeliveryCategory.Post) {
      setPostGroup(
        (prev) => prev ?? activeCategory.PostGroups[0]?.PostGroup ?? null,
      );
    } else {
      setPostGroup(null);
    }
  }, [activeCategory]);

  // Gdy zakładka nie wspiera bieżącego podwidoku — wróć na „Do wysyłki".
  useEffect(() => {
    if (!activeCategory) return;
    if (subView === "charts" && !activeCategory.SupportsDispatchChart)
      setSubView("dispatch");
    if (
      subView === "reception" &&
      !activeCategory.SupportsReceptionConfirmation
    )
      setSubView("dispatch");
  }, [activeCategory, subView]);

  const invalidate = () => {
    queryClient.invalidateQueries(["outgoing.categories"]);
    queryClient.invalidateQueries(["outgoing.dispatchList"]);
    queryClient.invalidateQueries(["outgoing.charts"]);
    queryClient.invalidateQueries(["outgoing.pendingReception"]);
  };

  const isEpuap = activeCategory?.Category === EParcelDeliveryCategory.Epuap;

  return (
    <div className={styles.wrap}>
      {/* Zakładki kategorii */}
      <div className={styles.categoryTabs}>
        {categories.map((c) => {
          const total =
            c.ReadyToDispatchCount + c.ChartCount + c.PendingReceptionCount;
          return (
            <button
              key={c.Category}
              className={`${styles.catTab} ${
                c.Category === activeCategoryId ? styles.catTabActive : ""
              }`}
              onClick={() => {
                setActiveCategoryId(c.Category);
                setSubView("dispatch");
              }}
            >
              {c.Name}
              {total > 0 && <span className={styles.badge}>{total}</span>}
            </button>
          );
        })}
      </div>

      {isEpuap ? (
        <div className={styles.placeholder}>
          Integracja ePUAP nie jest jeszcze dostępna w Nocie. Zakładka
          przygotowana pod przyszłe wdrożenie.
        </div>
      ) : activeCategory ? (
        <>
          {/* Przełącznik podwidoków (zależny od możliwości kategorii) */}
          <div className={styles.subTabs}>
            {(["dispatch", "charts", "reception"] as SubView[])
              .filter(
                (sv) =>
                  sv === "dispatch" ||
                  (sv === "charts" && activeCategory.SupportsDispatchChart) ||
                  (sv === "reception" &&
                    activeCategory.SupportsReceptionConfirmation),
              )
              .map((sv) => (
                <button
                  key={sv}
                  className={`${styles.subTab} ${
                    subView === sv ? styles.subTabActive : ""
                  }`}
                  onClick={() => setSubView(sv)}
                >
                  {SUBVIEW_LABELS[sv]}
                </button>
              ))}
          </div>

          {/* Wewnętrzny przełącznik Poczty (polecone/ZPO vs zwykłe) */}
          {subView === "dispatch" &&
            activeCategory.Category === EParcelDeliveryCategory.Post &&
            activeCategory.PostGroups.length > 1 && (
              <div className={styles.postGroups}>
                {activeCategory.PostGroups.map((pg) => (
                  <button
                    key={pg.PostGroup}
                    className={`${styles.pgBtn} ${
                      postGroup === pg.PostGroup ? styles.pgBtnActive : ""
                    }`}
                    onClick={() => setPostGroup(pg.PostGroup)}
                  >
                    {pg.Name}
                    {pg.Count > 0 && (
                      <span className={styles.badgeSm}>{pg.Count}</span>
                    )}
                  </button>
                ))}
              </div>
            )}

          <div className={styles.content}>
            {subView === "dispatch" && (
              <DispatchList
                category={activeCategory}
                postGroup={postGroup}
                searchText={searchText}
                onChanged={invalidate}
              />
            )}
            {subView === "charts" && (
              <ChartsList
                category={activeCategory}
                searchText={searchText}
                onChanged={invalidate}
              />
            )}
            {subView === "reception" && (
              <ReceptionList
                category={activeCategory}
                searchText={searchText}
                onChanged={invalidate}
              />
            )}
          </div>
        </>
      ) : (
        <div className={styles.placeholder}>Brak kategorii wychodzących.</div>
      )}
    </div>
  );
};

// ── Podwidok „Do wysyłki" (wielowybór → utwórz kartę nadawczą) ────────────────

const DispatchList = ({
  category,
  postGroup,
  searchText,
  onChanged,
}: {
  category: IOutgoingCategory;
  postGroup: number | null;
  searchText: string;
  onChanged: () => void;
}) => {
  const { data, isLoading } = dispatchChartService.useGetDispatchList({
    Category: category.Category,
    PostGroup: postGroup,
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [changeKind, setChangeKind] = useState<IDispatchListItem | null>(null);
  const [preview, setPreview] = useState<{
    id: string;
    title?: string | null;
  } | null>(null);

  const isTeamView = data?.data?.IsTeamView ?? false;
  const items: IDispatchListItem[] = useMemo(() => {
    const all = data?.data?.Items ?? [];
    const q = searchText.trim().toLowerCase();
    if (!q) return all;
    return all.filter((x) =>
      [x.ReceiverName, x.Title, x.ParcelNumber, x.ReceiverAddress, x.AuthorName]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q)),
    );
  }, [data, searchText]);

  // Wyczyść selekcję gdy zmienia się lista.
  useEffect(() => setSelected(new Set()), [category.Category, postGroup]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectedItems = items.filter((x) => selected.has(x.Id));
  // Karta nadawcza = jedna forma doręczenia.
  const kinds = new Set(selectedItems.map((x) => x.DeliveryKindId));
  const mixedKinds = kinds.size > 1;
  const canCreate =
    category.SupportsDispatchChart && selectedItems.length > 0 && !mixedKinds;

  return (
    <>
      <div className={styles.listToolbar}>
        <div className={styles.toolbarInfo}>
          {selected.size > 0
            ? `Zaznaczono: ${selected.size}`
            : `Przesyłek: ${items.length}`}
          {mixedKinds && (
            <span className={styles.warn}>
              Do jednej karty wybierz przesyłki tej samej formy.
            </span>
          )}
        </div>
        {category.SupportsDispatchChart && (
          <Button
            text="Utwórz kartę nadawczą"
            onClick={() => setShowCreate(true)}
            disabled={!canCreate}
          />
        )}
      </div>

      {isLoading ? (
        <div className={styles.empty}>Ładowanie…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>Brak przesyłek do wysyłki.</div>
      ) : (
        <div className={styles.table}>
          {items.map((it) => (
            <label
              key={it.Id}
              className={styles.row}
              title="Dwuklik — podgląd pisma"
              onDoubleClick={() => setPreview({ id: it.Id, title: it.Title })}
            >
              <input
                type="checkbox"
                checked={selected.has(it.Id)}
                onChange={() => toggle(it.Id)}
              />
              <div className={styles.rowMain}>
                <div className={styles.rowTitle}>
                  {it.ReceiverName ?? "—"}
                  {it.ParcelNumber && (
                    <span className={styles.sig}>{it.ParcelNumber}</span>
                  )}
                </div>
                <div className={styles.rowSub}>
                  {it.Title ?? ""}
                  {it.ReceiverAddress ? ` · ${it.ReceiverAddress}` : ""}
                </div>
              </div>
              <div className={styles.rowMeta}>
                <div>{it.DeliveryKindName}</div>
                {isTeamView && it.AuthorName && (
                  <div className={styles.author}>{it.AuthorName}</div>
                )}
              </div>
              <OutgoingRowMenu item={it} onChangeKind={setChangeKind} />
            </label>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateDispatchChartModal
          parcels={selectedItems}
          category={category}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            setSelected(new Set());
            onChanged();
          }}
        />
      )}

      {changeKind && (
        <ChangeDeliveryKindModal
          item={changeKind}
          onClose={() => setChangeKind(null)}
          onDone={() => {
            setChangeKind(null);
            onChanged();
          }}
        />
      )}

      {preview && (
        <ParcelPreviewModal
          parcelId={preview.id}
          title={preview.title}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
};

// ── Podwidok „Karty nadawcze" ─────────────────────────────────────────────────

const ChartsList = ({
  category,
  searchText,
  onChanged,
}: {
  category: IOutgoingCategory;
  searchText: string;
  onChanged: () => void;
}) => {
  const { data, isLoading } = dispatchChartService.useGetDispatchCharts({
    Category: category.Category,
    IncludeDispatched: false,
  });
  const { mutate: removeChart } = dispatchChartService.useRemoveFromChart();
  const [processChart, setProcessChart] = useState<IDispatchChart | null>(null);
  const [preview, setPreview] = useState<{
    id: string;
    title?: string | null;
  } | null>(null);

  const isTeamView = data?.data?.IsTeamView ?? false;
  const charts: IDispatchChart[] = useMemo(() => {
    const all = data?.data?.Charts ?? [];
    const q = searchText.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (c) =>
        c.Signature.toLowerCase().includes(q) ||
        (c.CreatorName ?? "").toLowerCase().includes(q) ||
        c.Parcels.some((p) => (p.ReceiverName ?? "").toLowerCase().includes(q)),
    );
  }, [data, searchText]);

  // Wydruk karty nadawczej — odwzorowanie „pocztowej książki nadawczej" z Magistratu
  // (ParcelsPdfHelper.PrintDispatchChart): A4 poziomo, kolumny urzędowego druku, opłata
  // rozbita na zł/gr. Doręczenie osobiste → wariant instytucjonalny (bez opłat, Podpis/Data).
  const doPrint = (chart: IDispatchChart) => {
    const institutional = chart.ChartTypeCode === "DO";

    // „Dokładne miejsce doręczenia" = adres [+ sygnatura przesyłki] [+ tytuł].
    const place = (p: IDispatchChart["Parcels"][number]) =>
      [p.ReceiverAddress, p.ParcelNumber, p.Title]
        .filter(Boolean)
        .map((v) => esc(v))
        .join(", ");

    const fee = (v?: number | null) => {
      if (v == null) return { zl: "", gr: "" };
      const zl = Math.trunc(v);
      const gr = Math.round((v - zl) * 100);
      return { zl: String(zl), gr: String(gr).padStart(2, "0") };
    };

    const sender = `${esc(chart.SenderName)} (${esc(chart.Signature)})`;

    const rows = institutional
      ? chart.Parcels.map(
          (p, i) =>
            `<tr><td class="c">${i + 1}</td><td>${esc(
              p.ReceiverName,
            )}</td><td>${place(p)}</td><td></td><td></td></tr>`,
        ).join("")
      : chart.Parcels.map((p, i) => {
          const f = fee(p.Fee);
          return `<tr><td class="c">${i + 1}</td><td>${esc(
            p.ReceiverName,
          )}</td><td>${place(
            p,
          )}</td><td></td><td></td><td></td><td></td><td></td><td></td><td class="c">${
            f.zl
          }</td><td class="c">${f.gr}</td><td></td><td></td></tr>`;
        }).join("");

    const head = institutional
      ? `<tr class="s"><td colspan="2" class="sl">Imię i nazwisko (nazwa) oraz adres nadawcy</td><td colspan="3" class="sv">${sender}</td></tr>
         <tr><th style="width:8mm">Lp.</th><th>ADRESAT<br>(imię i nazwisko lub nazwa)</th><th>Dokładne miejsce doręczenia</th><th style="width:60mm">Podpis</th><th style="width:24mm">Data</th></tr>`
      : `<tr class="s"><td colspan="3" class="sl">Imię i nazwisko (nazwa) oraz adres nadawcy</td><td colspan="10" class="sv">${sender}</td></tr>
         <tr>
           <th rowspan="2" style="width:8mm">Lp.</th>
           <th rowspan="2">ADRESAT<br>(imię i nazwisko lub nazwa)</th>
           <th rowspan="2">Dokładne miejsce doręczenia</th>
           <th colspan="2">Kwota zadekl. wartości</th>
           <th colspan="2">Masa</th>
           <th rowspan="2" style="width:34mm">Nr nadawczy</th>
           <th rowspan="2" style="width:18mm">Uwagi</th>
           <th colspan="2">Opłata</th>
           <th colspan="2">Kwota pobrania</th>
         </tr>
         <tr>
           <th>zł</th><th>gr</th><th>kg</th><th>g</th><th>zł</th><th>gr</th><th>zł</th><th>gr</th>
         </tr>`;

    const html = `<!doctype html><html lang="pl"><head><meta charset="utf-8"><title>Karta nadawcza ${esc(
      chart.Signature,
    )}</title><style>
      @page{size:A4 landscape;margin:12mm}
      body{font-family:Arial,Helvetica,sans-serif;color:#000;margin:0}
      table{width:100%;border-collapse:collapse;font-size:10px}
      th,td{border:1px solid #000;padding:3px 5px;vertical-align:top}
      th{background:#f0f0f0;font-size:9px;text-align:center;font-weight:700}
      td{height:12mm}
      td.c{text-align:center}
      tr.s td{border:none;height:auto;padding:2px 4px 8px}
      td.sl{font-weight:700;font-size:11px}
      td.sv{text-align:right;font-size:11px;font-weight:700}
      @media print{button{display:none}}
    </style></head><body>
      <table><thead>${head}</thead><tbody>${rows}</tbody></table>
      <script>window.onload=function(){window.print()}</script>
    </body></html>`;

    const w = window.open("", "_blank");
    if (!w) {
      notification({
        type: ENotificationType.ERROR,
        title: "Nie udało się otworzyć wydruku",
        text: "Zezwól na wyskakujące okna dla tej strony.",
      });
      return;
    }
    w.document.write(html);
    w.document.close();
  };

  const doRemove = (chartId: string) =>
    removeChart(
      { ChartId: chartId },
      {
        onSuccess: () => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Usunięto kartę",
            text: "Przesyłki wróciły na listę „Do wysyłki”.",
          });
          onChanged();
        },
        onError: () =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: "Nie udało się usunąć karty.",
          }),
      },
    );

  if (isLoading) return <div className={styles.empty}>Ładowanie…</div>;
  if (charts.length === 0)
    return <div className={styles.empty}>Brak kart nadawczych.</div>;

  return (
    <>
      <div className={styles.charts}>
        {charts.map((c) => (
          <div key={c.Id} className={styles.chartCard}>
            <div className={styles.chartHead}>
              <div>
                <div className={styles.chartSig}>{c.Signature}</div>
                <div className={styles.chartMeta}>
                  {c.DeliveryKindName} · {c.ParcelCount} szt.
                  {(() => {
                    const total = c.Parcels.reduce(
                      (s, p) => s + (p.Fee ?? 0),
                      0,
                    );
                    return total > 0 ? ` · opłata: ${fmtPln(total)}` : "";
                  })()}{" "}
                  · utworzono {fmtDate(c.CreationDate)}
                  {isTeamView && c.CreatorName
                    ? ` · autor: ${c.CreatorName}`
                    : ""}
                </div>
              </div>
              <div className={styles.chartActions}>
                <HollowButton text="Drukuj" onClick={() => doPrint(c)} />
                <Button
                  text="Potwierdź wysyłkę"
                  onClick={() => setProcessChart(c)}
                />
                <HollowButton text="Usuń" onClick={() => doRemove(c.Id)} />
              </div>
            </div>
            <div className={styles.chartParcels}>
              {c.Parcels.map((p) => (
                <div
                  key={p.Id}
                  className={styles.chartParcel}
                  title="Dwuklik — podgląd pisma"
                  onDoubleClick={() => setPreview({ id: p.Id, title: p.Title })}
                >
                  <span className={styles.sig}>{p.ParcelNumber ?? "—"}</span>
                  <span>{p.ReceiverName ?? "—"}</span>
                  <span className={styles.dim}>{p.ReceiverAddress ?? ""}</span>
                  {p.Fee != null && (
                    <span className={styles.fee}>{fmtPln(p.Fee)}</span>
                  )}
                  {isTeamView && p.AuthorName && (
                    <span className={styles.author}>{p.AuthorName}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {processChart && (
        <ProcessDispatchChartModal
          chart={processChart}
          onClose={() => setProcessChart(null)}
          onDone={() => {
            setProcessChart(null);
            onChanged();
          }}
        />
      )}

      {preview && (
        <ParcelPreviewModal
          parcelId={preview.id}
          title={preview.title}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
};

// ── Podwidok „Zwrotki ZPO" ────────────────────────────────────────────────────

const ReceptionList = ({
  category,
  searchText,
  onChanged,
}: {
  category: IOutgoingCategory;
  searchText: string;
  onChanged: () => void;
}) => {
  const { data, isLoading } = dispatchChartService.useGetPendingReception({
    Category: category.Category,
  });
  const { mutate: resend } = dispatchChartService.useResendParcel();
  const [reception, setReception] = useState<IPendingReceptionItem | null>(
    null,
  );
  const [preview, setPreview] = useState<{
    id: string;
    title?: string | null;
  } | null>(null);

  const isTeamView = data?.data?.IsTeamView ?? false;
  const items: IPendingReceptionItem[] = useMemo(() => {
    const all = data?.data?.Items ?? [];
    const q = searchText.trim().toLowerCase();
    if (!q) return all;
    return all.filter((x) =>
      [x.ReceiverName, x.Title, x.ParcelNumber, x.AuthorName]
        .filter(Boolean)
        .some((v) => v!.toLowerCase().includes(q)),
    );
  }, [data, searchText]);

  const doResend = (id: string) =>
    resend(
      { ParcelId: id },
      {
        onSuccess: () => {
          notification({
            type: ENotificationType.SUCCESS,
            title: "Utworzono ponowną przesyłkę",
            text: "Nowa przesyłka trafiła na listę „Do wysyłki”.",
          });
          onChanged();
        },
        onError: () =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: "Nie udało się utworzyć ponownej przesyłki.",
          }),
      },
    );

  if (isLoading) return <div className={styles.empty}>Ładowanie…</div>;
  if (items.length === 0)
    return (
      <div className={styles.empty}>
        Brak przesyłek ZPO oczekujących na potwierdzenie odbioru.
      </div>
    );

  return (
    <>
      <div className={styles.table}>
        {items.map((it) => (
          <div
            key={it.Id}
            className={styles.row}
            title="Dwuklik — podgląd pisma"
            onDoubleClick={() => setPreview({ id: it.Id, title: it.Title })}
          >
            <div className={styles.rowMain}>
              <div className={styles.rowTitle}>
                {it.ReceiverName ?? "—"}
                {it.ParcelNumber && (
                  <span className={styles.sig}>{it.ParcelNumber}</span>
                )}
              </div>
              <div className={styles.rowSub}>
                {it.Title ?? ""} · nadano {fmtDate(it.DispatchDate)}
                {isTeamView && it.AuthorName
                  ? ` · autor: ${it.AuthorName}`
                  : ""}
              </div>
            </div>
            <div className={styles.rowActions}>
              <Button
                text="Zarejestruj odbiór"
                onClick={() => setReception(it)}
              />
              <HollowButton
                text="Wyślij ponownie"
                onClick={() => doResend(it.Id)}
              />
            </div>
          </div>
        ))}
      </div>

      {reception && (
        <ReceptionModal
          item={reception}
          onClose={() => setReception(null)}
          onDone={() => {
            setReception(null);
            onChanged();
          }}
        />
      )}

      {preview && (
        <ParcelPreviewModal
          parcelId={preview.id}
          title={preview.title}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
};

export default OutgoingView;
